import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getTags , createQuestionBank , getQuestionBank, updateQuestionBank, deleteQuestionBank, getQuestionBankById , deleteQuestionTag } from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';
import DataTable from '../../../components/admin/DataTable';

function QuestionBank() {
  const { permissions } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Data states
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    question: '',
    questionType: 'MCQ',
    categories: [],
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]
  });

  const [editFormData, setEditFormData] = useState({
    id: '',
    question: '',
    questionType: 'MCQ',
    categories: [],
    options: []
  });

  // Category input state
  const [categoryInput, setCategoryInput] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Selected question for delete
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // Tag deletion states
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [selectedTagToDelete, setSelectedTagToDelete] = useState(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);

  // Modal handlers
  const handleModalClose = () => {
    setShowAddModal(false);
    setFormErrors({});
    setFormData({
      question: '',
      questionType: 'MCQ',
      categories: [],
      options: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ]
    });
    setCategoryInput('');
    setShowCategoryDropdown(false);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setFormErrors({});
    setEditFormData({
      id: '',
      question: '',
      questionType: 'MCQ',
      categories: [],
      options: []
    });
    setCategoryInput('');
    setShowCategoryDropdown(false);
  };

  // Form input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Category handlers
  const handleCategoryKeyDown = (e) => {
    if (e.key === 'Enter' && categoryInput.trim()) {
      e.preventDefault();
      if (!formData.categories.includes(categoryInput.trim())) {
        setFormData(prev => ({
          ...prev,
          categories: [...prev.categories, categoryInput.trim()]
        }));
      }
      setCategoryInput('');
    }
  };

  const removeCategory = (categoryToRemove) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(cat => cat !== categoryToRemove)
    }));
  };

  // Select category from dropdown
  const selectCategory = (categoryName) => {
    if (!formData.categories.includes(categoryName)) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryName]
      }));
    }
    setCategoryInput('');
    setShowCategoryDropdown(false);
  };

  // Add new category
  const addNewCategory = () => {
    if (categoryInput.trim() && !formData.categories.includes(categoryInput.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryInput.trim()]
      }));
    }
    setCategoryInput('');
    setShowCategoryDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Keep dropdown open when clicking anywhere inside the category wrapper
      if (!event.target.closest('[data-category-dropdown-wrapper="true"]')) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Option handlers
  const handleOptionChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((option, idx) =>
        idx === index ? { ...option, text: value } : option
      )
    }));
  };

  const handleCorrectAnswerChange = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((option, idx) => ({
        ...option,
        isCorrect: idx === index
      }))
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false }]
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) {
      toast.warn('At least 2 options are required');
      return;
    }

    setFormData(prev => {
      const newOptions = prev.options.filter((_, idx) => idx !== index);
      // If we removed the correct answer, clear all correct flags
      const removedCorrect = prev.options[index].isCorrect;
      if (removedCorrect) {
        return {
          ...prev,
          options: newOptions.map(opt => ({ ...opt, isCorrect: false }))
        };
      }
      return {
        ...prev,
        options: newOptions
      };
    });
  };

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.question.trim()) {
      errors.question = 'Question is required';
    }

    if (formData.categories.length === 0) {
      errors.categories = 'At least one tag is required';
    }

    const filledOptions = formData.options.filter(opt => opt.text.trim());
    if (filledOptions.length < 2) {
      errors.options = 'At least 2 options are required';
    }

    const hasCorrectAnswer = formData.options.some(opt => opt.isCorrect && opt.text.trim());
    if (!hasCorrectAnswer) {
      errors.correctAnswer = 'Please select a correct answer';
    }

    return errors;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!permissions.question_bank_management_create) {
      toast.warning("You don't have permission to create questions");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform form data to API format
      const apiData = {
        question_name: formData.question,
        question_type: formData.questionType.toLowerCase(),
        options: formData.options
          .filter(option => option.text.trim()) // Only include filled options
          .map((option, index) => ({
            option_number: index + 1,
            option_value: option.text.trim(),
            is_correct: option.isCorrect
          })),
        subject: "", // Empty as per existing pattern
        modules: "", // Empty as per existing pattern
        tags: formData.categories // Pass the selected categories as tags
      };

      // console.log('Question data to save:', apiData);

      const response = await createQuestionBank(apiData);

      if (response.success) {
        toast.success(response.message || 'Question added successfully!');
        handleModalClose();
        fetchQuestions(currentPage, searchTerm); // Refresh questions list
        fetchTags(); // Refresh tags to get newly created tags
      } else {
        toast.error(response.message || 'Failed to add question');
      }

    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch questions from API
  const fetchQuestions = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const payload = {
        page: page,
        limit: itemsPerPage,
        search: search
      };

      const response = await getQuestionBank(payload);
      // console.log('Questions API Response:', response);

      if (response.success) {
        // Handle different response structures
        let questionsData = [];
        if (response.data && response.data.questions) {
          questionsData = response.data.questions;
        } else if (response.questions) {
          questionsData = response.questions;
        } else if (Array.isArray(response.data)) {
          questionsData = response.data;
        }

        setQuestions(questionsData);
      } else {
        toast.error(response.message || "Failed to fetch questions");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("An error occurred while fetching questions");
    } finally {
      setLoading(false);
    }
  };

  // Fetch available tags from API
  const fetchTags = async () => {
    try {
      const response = await getTags();
      // console.log('Tags API Response:', response);

      if (response.success && response.data) {
        setAvailableTags(response.data);
      } else {
        console.error("Failed to fetch tags:", response);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchQuestions(1, '');
    fetchTags();
  }, [itemsPerPage]);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    fetchQuestions(1, value);
  };

  // Handle edit question
  const handleEditQuestion = (question) => {
    if (!permissions.question_bank_management_edit) {
      toast.warning("You don't have permission to edit questions");
      return;
    }
    // Transform API data to form format
    setEditFormData({
      id: question.id,
      question: question.question_name,
      questionType: question.question_type.toUpperCase(),
      categories: question.tags || [],
      options: question.options.map(opt => ({
        text: opt.option_value,
        isCorrect: opt.is_correct
      }))
    });
    setShowEditModal(true);
  };

  // Handle delete question
  const handleDeleteQuestion = (question) => {
    if (!permissions.question_bank_management_delete) {
      toast.warning("You don't have permission to delete questions");
      return;
    }
    setSelectedQuestion(question);
    setShowDeleteModal(true);
  };

  // Confirm delete question
  const confirmDeleteQuestion = async () => {
    if (!selectedQuestion) return;

    setIsDeleting(true);
    try {
      const response = await deleteQuestionBank({ question_id: selectedQuestion.id });

      if (response.success) {
        toast.success(response.message || 'Question deleted successfully');
        setShowDeleteModal(false);
        setSelectedQuestion(null);
        fetchQuestions(currentPage, searchTerm);
      } else {
        toast.error(response.message || 'Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle edit submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Validate edit form data
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsEditing(true);

    try {
      // Transform edit form data to API format
      const apiData = {
        question_id: editFormData.id,
        question_name: editFormData.question,
        question_type: editFormData.questionType.toLowerCase(),
        options: editFormData.options
          .filter(option => option.text.trim()) // Only include filled options
          .map((option, index) => ({
            option_number: index + 1,
            option_value: option.text.trim(),
            is_correct: option.isCorrect
          })),
        subject: "", // Empty as per existing pattern
        modules: "", // Empty as per existing pattern
        tags: editFormData.categories, // Pass the selected categories as tags
        is_active: true
      };

      // console.log('Edit question data to save:', apiData);

      const response = await updateQuestionBank(apiData);

      if (response.success) {
        toast.success(response.message || 'Question updated successfully!');
        handleEditModalClose();
        fetchQuestions(currentPage, searchTerm); // Refresh questions list
        fetchTags(); // Refresh tags to get newly created tags
      } else {
        toast.error(response.message || 'Failed to update question');
      }

    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };

  // Validate edit form
  const validateEditForm = () => {
    const errors = {};

    if (!editFormData.question.trim()) {
      errors.question = 'Question is required';
    }

    if (editFormData.categories.length === 0) {
      errors.categories = 'At least one tag is required';
    }

    const filledOptions = editFormData.options.filter(opt => opt.text.trim());
    if (filledOptions.length < 2) {
      errors.options = 'At least 2 options are required';
    }

    const hasCorrectAnswer = editFormData.options.some(opt => opt.isCorrect && opt.text.trim());
    if (!hasCorrectAnswer) {
      errors.correctAnswer = 'Please select a correct answer';
    }

    return errors;
  };

  // Handle tag deletion
  const handleDeleteTag = (tag) => {
    setSelectedTagToDelete(tag);
    setShowDeleteTagModal(true);
  };

  // Confirm tag deletion
  const confirmDeleteTag = async () => {
    if (!selectedTagToDelete) return;

    setIsDeletingTag(true);
    try {
      const response = await deleteQuestionTag({ id: selectedTagToDelete.id });

      if (response.success) {
        toast.success(response.message || `Tag "${selectedTagToDelete.tag_name}" deleted successfully`);
        setShowDeleteTagModal(false);
        setSelectedTagToDelete(null);

        // Refresh both tags and questions since questions might be affected
        fetchTags();
        fetchQuestions(currentPage, searchTerm);
      } else {
        toast.error(response.message || 'Failed to delete tag');
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag. Please try again.');
    } finally {
      setIsDeletingTag(false);
    }
  };

  return (
    <>
      {/* search and add question */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="w-full md:max-w-xs">
          <input
            type="text"
            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="w-full md:w-auto flex justify-start md:justify-end">
          <button
            type="button"
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg text-sm inline-flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minWidth: '200px' }}
            onClick={() => {
              if (!permissions.question_bank_management_create) {
                toast.warning("You don't have permission to create questions");
                return;
              }
              setShowAddModal(true);
            }}
            disabled={!permissions.question_bank_management_create}
          >
            <Icon icon="fluent:add-24-regular" width="16" height="16" />
            Add Question
          </button>
        </div>
      </div>

      {/* table – wrapped with DataTable for consistency */}
      <div className="mb-3">
        <DataTable
          loading={loading}
          hasRows={questions.length > 0}
          caption="Loading Questions..."
          minHeight="400px"
          emptyMessage="No questions found"
        >
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-center w-20">
                  SL. No.
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-left min-w-[260px]">
                  Question
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-center w-28">
                  Type
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-left min-w-[220px]">
                  Options
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-left w-40">
                  Tags
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-center w-32">
                  Created At
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 text-center w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {questions.map((question, index) => (
                <tr key={question.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-center align-middle text-gray-700">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-800">
                    {question.question_name}
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {question.question_type?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex flex-col gap-1">
                      {question.options?.slice(0, 3).map((option, idx) => (
                        <div
                          key={idx}
                          className={`text-xs sm:text-sm p-2 rounded border ${
                            option.is_correct
                              ? 'bg-green-50 border-green-200 text-green-800'
                              : 'bg-gray-50 border-gray-200 text-gray-700'
                          }`}
                        >
                          {option.option_value}
                          {option.is_correct && (
                            <Icon
                              icon="fluent:checkmark-circle-24-filled"
                              className="ml-2 text-green-600 inline-block"
                              width="16"
                              height="16"
                            />
                          )}
                        </div>
                      ))}
                      {question.options?.length > 3 && (
                        <small className="text-gray-500 text-xs">
                          +{question.options.length - 3} more
                        </small>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex flex-wrap gap-1">
                      {question.tags?.slice(0, 2).map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                        >
                          {tag}
                        </span>
                      ))}
                      {question.tags?.length > 2 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          +{question.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center align-middle text-gray-700 text-xs sm:text-sm">
                    {new Date(question.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex justify-center gap-2">
                      <button
                        className="border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg text-xs sm:text-sm w-9 h-9 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        title={permissions.question_bank_management_edit ? 'Edit' : "You don't have permission to edit"}
                        onClick={() => handleEditQuestion(question)}
                        disabled={!permissions.question_bank_management_edit}
                      >
                        <Icon icon="fluent:edit-24-regular" width="16" height="16" />
                      </button>
                      <button
                        className="border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-xs sm:text-sm w-9 h-9 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        title={permissions.question_bank_management_delete ? 'Delete' : "You don't have permission to delete"}
                        onClick={() => handleDeleteQuestion(question)}
                        disabled={!permissions.question_bank_management_delete}
                      >
                        <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </div>

      {/* record per page , total record , pagination – match AllCourses */}
      <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <span className="hidden sm:inline">Rows per page:</span>
          <select
            className="px-3 py-1.5 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
              fetchQuestions(1, searchTerm);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="text-gray-500">
            {questions.length === 0
              ? 'No records'
              : `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(
                  currentPage * itemsPerPage,
                  questions.length
                )} of ${questions.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              const newPage = Math.max(currentPage - 1, 1);
              setCurrentPage(newPage);
              fetchQuestions(newPage, searchTerm);
            }}
            disabled={currentPage === 1 || Math.ceil(questions.length / itemsPerPage) === 0 || loading}
            title="Previous page"
          >
            <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
          </button>
          <span className="px-2 text-gray-700">
            Page <span className="font-medium">{questions.length === 0 ? 0 : currentPage}</span> of{' '}
            <span className="font-medium">{Math.ceil(questions.length / itemsPerPage) || 0}</span>
          </span>
          <button
            type="button"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              const totalPages = Math.ceil(questions.length / itemsPerPage);
              const newPage = Math.min(currentPage + 1, totalPages);
              setCurrentPage(newPage);
              fetchQuestions(newPage, searchTerm);
            }}
            disabled={currentPage >= Math.ceil(questions.length / itemsPerPage) || Math.ceil(questions.length / itemsPerPage) === 0 || loading}
            title="Next page"
          >
            <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
          </button>
        </div>
      </div>

      {/* Add Question Modal */}
{showAddModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h5 className="text-lg font-medium">Add New Question</h5>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-gray-600"
                                onClick={handleModalClose}
                            >×</button>
                        </div>
                        <div className="p-4" style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '0' }}>
          {!permissions.question_bank_management_create ? (
            <div className="p-3 rounded-lg borderwarning">
              <Icon icon="mdi:warning" className="mr-2" />
              You don't have permission to create new questions.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Question Input */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Enter Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm ${
                    formErrors.question ? 'border-red-500' : 'border-input-border'
                  }`}
                  name="question"
                  value={formData.question}
                  onChange={handleInputChange}
                  placeholder="Enter your question here..."
                  rows="3"
                />
                {formErrors.question && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.question}</p>
                )}
              </div>

              {/* Question Type + Categories */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Question Type */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Question Type</label>
                  <select
                    className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm bg-white"
                    name="questionType"
                    value={formData.questionType}
                    onChange={handleInputChange}
                  >
                    <option value="MCQ">Multiple Choice Question (MCQ)</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Currently only MCQ type is supported
                  </p>
                </div>

                {/* Tags */}
                <div className="space-y-1.5" data-category-dropdown-wrapper="true">
                  <label className="block text-sm font-medium text-gray-700">
                    Tags <span className="text-red-500">*</span>
                  </label>

                  {/* Dropdown for available tags */}
                  <div className="relative">
                    <input
                      type="text"
                      className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm ${
                        formErrors.categories ? 'border-red-500' : 'border-input-border'
                      }`}
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      onKeyDown={handleCategoryKeyDown}
                      placeholder="Type to search tags or add new..."
                      onFocus={() => setShowCategoryDropdown(true)}
                    />

                    {/* Dropdown with available tags */}
                    {showCategoryDropdown && (
                      <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-[1050] max-h-[200px] overflow-y-auto" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1050 }}>
                        {/* Filtered available tags */}
                        {availableTags
                          .filter(tag =>
                            tag.tag_name.toLowerCase().includes(categoryInput.toLowerCase()) &&
                            !formData.categories.includes(tag.tag_name)
                          )
                          .map(tag => (
                            <div
                              key={tag.id}
                              className="flex justify-between items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-left w-full"
                              style={{ cursor: 'pointer', padding: '8px 12px' }}
                            >
                              <span
                                onClick={() => selectCategory(tag.tag_name)}
                                style={{ flex: 1 }}
                              >
                                {tag.tag_name}
                              </span>
                              <button
                                type="button"
                                className="text-sm text-red-600 hover:bg-red-50 p-1 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTag(tag);
                                }}
                                title="Delete tag permanently"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: 'none',
                                  background: 'transparent',
                                  fontSize: '16px',
                                  fontWeight: 'bold'
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))
                        }

                        {/* Add new tag option */}
                        {categoryInput.trim() &&
                         !availableTags.some(tag => tag.tag_name.toLowerCase() === categoryInput.toLowerCase()) && (
                          <div
                            className="px-3 py-2 text-primary hover:bg-gray-100 cursor-pointer"
                            onClick={() => addNewCategory()}
                            style={{ cursor: 'pointer' }}
                          >
                            <Icon icon="fluent:add-24-regular" width="16" height="16" className="mr-2" />
                            Add "{categoryInput.trim()}"
                          </div>
                        )}

                        {availableTags.length === 0 && (
                          <div className="px-3 py-2 text-gray-400 cursor-not-allowed">No tags available</div>
                        )}
                      </div>
                    )}
                  </div>

                  {formErrors.categories && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.categories}</p>
                  )}

                  {/* Display selected tags */}
                  {formData.categories.length > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.categories.map((category, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 gap-1">
                            {category}
                            <button
                              type="button"
                              className="p-1 rounded-full hover:bg-white/60 text-blue-800 flex items-center justify-center"
                              style={{ fontSize: '0.7em', lineHeight: 1 }}
                              onClick={() => removeCategory(category)}
                              aria-label="Remove tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Options */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <label className="text-sm font-medium text-gray-700 m-0">
                    Options <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-1 bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg text-sm"
                    onClick={addOption}
                  >
                    <Icon icon="fluent:add-24-regular" width="16" height="16" />
                    Add Option
                  </button>
                </div>

                {formErrors.options && (
                  <p className="text-xs text-red-500 mb-1">{formErrors.options}</p>
                )}
                {formErrors.correctAnswer && (
                  <p className="text-xs text-red-500 mb-1">{formErrors.correctAnswer}</p>
                )}

                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2 w-full">
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={option.isCorrect}
                        onChange={() => handleCorrectAnswerChange(index)}
                        title="Mark as correct answer"
                        className="w-4 h-4 flex-shrink-0"
                      />
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                      />
                      {formData.options.length > 2 && (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center border border-red-500 text-red-600 hover:bg-red-50 rounded-lg text-sm w-9 h-9 flex-shrink-0"
                          onClick={() => removeOption(index)}
                          title="Remove option"
                        >
                          <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500">
                  Select the radio button to mark the correct answer. Minimum 2 options required.
                </p>
              </div>
            </form>
          )}
        </div>
        <div className="flex justify-between border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
            onClick={handleModalClose}
            disabled={isSubmitting}
            style={{ width: '100%' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ width: '100%' }}
          >
            {isSubmitting ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              'Add Question'
            )}
          </button>
        </div>
      </div>
    </div>
)}

      {/* Edit Question Modal */}
      {showEditModal && (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={handleEditModalClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h5 className="text-lg font-medium">Edit Question</h5>
          <button type="button" className="text-gray-400 hover:text-gray-600 p-1" onClick={handleEditModalClose} aria-label="Close">×</button>
        </div>
        <div className="p-4 overflow-y-auto flex-1" style={{ maxHeight: '70vh' }}>
          {!permissions.question_bank_management_edit ? (
            <div className="p-3 rounded-lg borderwarning">
              <Icon icon="mdi:warning" className="mr-2" />
              You don't have permission to edit questions.
            </div>
          ) : (
            <form onSubmit={handleEditSubmit}>
              {/* Question Input */}
              <div className="mb-3">
                <label className="form-label">Enter Question <span className="text-red-500">*</span></label>
                <textarea
                  className={`form-control ${formErrors.question ? 'is-invalid' : ''}`}
                  name="question"
                  value={editFormData.question}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Enter your question here..."
                  rows="3"
                />
                {formErrors.question && (
                  <div className="invalid-feedback">{formErrors.question}</div>
                )}
              </div>

              {/* Question Type */}
              <div className="mb-3">
                <label className="form-label">Question Type</label>
                <select
                  className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  name="questionType"
                  value={editFormData.questionType}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, questionType: e.target.value }))}
                >
                  <option value="MCQ">Multiple Choice Question (MCQ)</option>
                </select>
                <div className="text-sm text-gray-500">
                  Question type cannot be changed after creation
                </div>
              </div>

              {/* Tags for Edit */}
              <div className="mb-3">
                <label className="form-label">Tags <span className="text-red-500">*</span></label>

                {/* Display selected tags */}
                {editFormData.categories.length > 0 && (
                  <div className="mb-2">
                    <div className="flex flex-wrap gap-2">
                      {editFormData.categories.map((category, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 gap-1">
                          {category}
                          <button
                            type="button"
                            className="p-1 rounded-full hover:bg-white/60 text-blue-800 flex items-center justify-center"
                            style={{ fontSize: '0.7em', lineHeight: 1 }}
                            onClick={() => {
                              setEditFormData(prev => ({
                                ...prev,
                                categories: prev.categories.filter(cat => cat !== category)
                              }));
                            }}
                            aria-label="Remove tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add more tags */}
                <div className="relative">
                  <input
                    type="text"
                    className={`form-control ${formErrors.categories ? 'is-invalid' : ''}`}
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && categoryInput.trim()) {
                        e.preventDefault();
                        if (!editFormData.categories.includes(categoryInput.trim())) {
                          setEditFormData(prev => ({
                            ...prev,
                            categories: [...prev.categories, categoryInput.trim()]
                          }));
                        }
                        setCategoryInput('');
                      }
                    }}
                    placeholder="Type to add more tags..."
                    onFocus={() => setShowCategoryDropdown(true)}
                  />

                  {/* Dropdown for edit modal */}
                  {showCategoryDropdown && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-[1050] max-h-[200px] overflow-y-auto" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1050 }}>
                      {availableTags
                        .filter(tag =>
                          tag.tag_name.toLowerCase().includes(categoryInput.toLowerCase()) &&
                          !editFormData.categories.includes(tag.tag_name)
                        )
                        .map(tag => (
                          <div
                            key={tag.id}
                            className="flex justify-between items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-left w-full"
                            style={{ cursor: 'pointer', padding: '8px 12px' }}
                          >
                            <span
                              onClick={() => {
                                if (!editFormData.categories.includes(tag.tag_name)) {
                                  setEditFormData(prev => ({
                                    ...prev,
                                    categories: [...prev.categories, tag.tag_name]
                                  }));
                                }
                                setCategoryInput('');
                                setShowCategoryDropdown(false);
                              }}
                              style={{ flex: 1 }}
                            >
                              {tag.tag_name}
                            </span>
                            <button
                              type="button"
                              className="text-sm text-red-600 hover:bg-red-50 p-1 rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTag(tag);
                              }}
                              title="Delete tag permanently"
                              style={{
                                width: '20px',
                                height: '20px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '16px',
                                fontWeight: 'bold'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>

                {formErrors.categories && (
                  <div className="invalid-feedback d-block">{formErrors.categories}</div>
                )}
              </div>

              {/* Options for Edit */}
              <div className="mb-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Options <span className="text-red-500">*</span></label>
                  <button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg text-sm"
                    onClick={() => {
                      setEditFormData(prev => ({
                        ...prev,
                        options: [...prev.options, { text: '', isCorrect: false }]
                      }));
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      fontSize: '14px',
                      width: '150px',
                    }}
                  >
                    <Icon icon="fluent:add-24-regular" width="16" height="16" />
                    Add Option
                  </button>
                </div>

                {formErrors.options && (
                  <div className="text-red-500 small mb-2">{formErrors.options}</div>
                )}
                {formErrors.correctAnswer && (
                  <div className="text-red-500 small mb-2">{formErrors.correctAnswer}</div>
                )}

                {editFormData.options.map((option, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <input
                        type="radio"
                        name="editCorrectAnswer"
                        checked={option.isCorrect}
                        onChange={() => {
                          setEditFormData(prev => ({
                            ...prev,
                            options: prev.options.map((opt, idx) => ({
                              ...opt,
                              isCorrect: idx === index
                            }))
                          }));
                        }}
                        title="Mark as correct answer"
                        style={{
                          margin: 0,
                          flexShrink: 0,
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                        value={option.text}
                        onChange={(e) => {
                          setEditFormData(prev => ({
                            ...prev,
                            options: prev.options.map((opt, idx) =>
                              idx === index ? { ...opt, text: e.target.value } : opt
                            )
                          }));
                        }}
                        placeholder={`Option ${index + 1}`}
                        style={{
                          flex: 1,
                          minWidth: 0
                        }}
                      />
                      {editFormData.options.length > 2 && (
                        <button
                          type="button"
                          className="border border-red-500 text-red-600 hover:bg-red-50 rounded-lg text-sm py-2 px-4"
                          onClick={() => {
                            if (editFormData.options.length > 2) {
                              setEditFormData(prev => ({
                                ...prev,
                                options: prev.options.filter((_, idx) => idx !== index)
                              }));
                            }
                          }}
                          title="Remove option"
                          style={{
                            flexShrink: 0,
                            width: '36px',
                            height: '36px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="text-sm text-gray-500">
                  Select the radio button to mark the correct answer. Minimum 2 options required.
                </div>
              </div>
            </form>
          )}
        </div>
        <div className="flex justify-between border-t border-gray-200 px-6 py-4 flex-shrink-0">
          <button
            type="button"
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
            onClick={handleEditModalClose}
            disabled={isEditing}
            style={{ width: '100%' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
            onClick={handleEditSubmit}
            disabled={isEditing || !permissions.question_bank_management_edit}
            style={{ width: '100%' }}
          >
            {isEditing ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              'Update Question'
            )}
          </button>
        </div>
      </div>
    </div>
)}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => { setShowDeleteModal(false); setSelectedQuestion(null); }}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h5 className="text-lg font-medium text-red-500 flex items-center gap-2">
            <Icon icon="fluent:warning-24-regular" />
            Confirm Deletion
          </h5>
          <button type="button" className="text-gray-400 hover:text-gray-600 p-1" onClick={() => { setShowDeleteModal(false); setSelectedQuestion(null); }} aria-label="Close">×</button>
        </div>
        <div className="p-4">
          {!permissions.question_bank_management_delete ? (
            <div className="p-3 rounded-lg borderwarning">
              <Icon icon="mdi:warning" className="mr-2" />
              You don't have permission to delete questions.
            </div>
          ) : selectedQuestion && (
            <div>
              <p>Are you sure you want to delete the question:</p>
              <p className="font-bold">"{selectedQuestion.question_name}"</p>
              <p className="text-red-500 mb-0">This action cannot be undone.</p>
            </div>
          )}
        </div>
        <div className="flex justify-between gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedQuestion(null);
            }}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
            onClick={confirmDeleteQuestion}
            disabled={isDeleting || !permissions.question_bank_management_delete}
          >
            {isDeleting ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              'Delete Question'
            )}
          </button>
        </div>
      </div>
    </div>
)}

      {/* Delete Tag Confirmation Modal */}
      {showDeleteTagModal && (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => { setShowDeleteTagModal(false); setSelectedTagToDelete(null); }}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h5 className="text-lg font-medium text-red-500 flex items-center gap-2">
            <Icon icon="fluent:warning-24-regular" />
            Delete Tag Permanently
          </h5>
          <button type="button" className="text-gray-400 hover:text-gray-600 p-1" onClick={() => { setShowDeleteTagModal(false); setSelectedTagToDelete(null); }} aria-label="Close">×</button>
        </div>
        <div className="p-4">
          {selectedTagToDelete && (
            <div>
              <p>Are you sure you want to permanently delete the tag:</p>
              <p className="fw-bold text-primary">"{selectedTagToDelete.tag_name}"</p>
              <div className="p-3 rounded-lg borderwarning">
                <Icon icon="fluent:warning-24-filled" className="mr-2" />
                <strong>Warning:</strong> This action will:
                <ul className="mb-0 mt-2">
                  <li>Delete this tag permanently from the system</li>
                  <li>Remove this tag from all existing questions</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
            onClick={() => {
              setShowDeleteTagModal(false);
              setSelectedTagToDelete(null);
            }}
            disabled={isDeletingTag}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
            onClick={confirmDeleteTag}
            disabled={isDeletingTag}
          >
            {isDeletingTag ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              <>
                <Icon icon="fluent:delete-24-regular" className="mr-2" />
                Delete Permanently
              </>
            )}
          </button>
        </div>
      </div>
    </div>
)}
    </>
  )
}

export default QuestionBank
